import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE } from '../config';

interface Inspection {
  id: string;
  vehicle_registration?: string;
  driver_name?: string;
  start_time: string;
  end_time?: string;
  duration?: number;
  status: string;
  defects_found: number;
  notes?: string;
  mileage?: string;
  fuel_level?: string;
  vehicle_height?: string;
  inspection_type?: string;
}

interface ChecklistItem {
  id: string;
  item_name: string;
  status: string; // 'checked', 'defect', 'unticked'
  notes?: string;
}

interface InspectionDetailsModalProps {
  inspection: Inspection;
  onClose: () => void;
}

const InspectionDetailsModal: React.FC<InspectionDetailsModalProps> = ({ inspection, onClose }) => {
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchChecklist = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_BASE}/inspections/${inspection.id}/checks`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setChecklist(response.data.data);
      } catch (err) {
        setChecklist([]);
      }
      setLoading(false);
    };
    fetchChecklist();
  }, [inspection.id]);

  const getStatusIcon = (status: string) => {
    if (status === 'checked') return <span className="text-green-600 text-xl mr-2">✓</span>;
    if (status === 'defect') return <span className="text-red-600 text-xl mr-2">✕</span>;
    return <span className="text-gray-400 text-xl mr-2">–</span>;
  };

  const formatVehicleHeight = (raw?: string) => {
    if (!raw) return 'N/A';
    const num = Number(raw);
    if (Number.isFinite(num)) {
      const feet = Math.floor(num);
      const inches = Math.round((num - feet) * 12);
      const normalizedFeet = inches === 12 ? feet + 1 : feet;
      const normalizedInches = inches === 12 ? 0 : inches;
      return `${normalizedFeet}'${normalizedInches}"`;
    }
    return raw;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="relative bg-white rounded-lg w-2/3 max-h-[80vh] overflow-hidden">
        <div className="sticky top-0 z-50 bg-white px-6 pt-6 pb-4 border-b border-gray-100">
          <div className="flex justify-between items-start">
            <h2 className="text-2xl font-bold">Inspection Details</h2>
            <button
              onClick={onClose}
              aria-label="Close"
              className="w-10 h-10 rounded-full bg-gray-900 text-white flex items-center justify-center shadow-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-400"
            >
              <span className="text-base font-semibold leading-none">×</span>
            </button>
          </div>
        </div>

        <div className="px-6 pb-6 overflow-y-auto max-h-[calc(80vh-76px)]">

          {/* Inspection Type Badge */}
          <div className="mb-4">
            <span className={`inline-block px-4 py-2 rounded-full text-sm font-semibold ${
              inspection.inspection_type === 'pre'
                ? 'bg-orange-100 text-orange-800'
                : 'bg-purple-100 text-purple-800'
            }`}>
              {inspection.inspection_type === 'pre' ? '🌅 Pre-Trip Inspection' : '🌙 Post-Trip Inspection'}
            </span>
          </div>

          {/* Pre-Inspection Data Section */}
          <div className="mb-6 bg-blue-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4 text-blue-900">Vehicle Details</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-600">Mileage</p>
                <p className="text-lg font-semibold">{inspection.mileage || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Fuel Level</p>
                <p className="text-lg font-semibold">{inspection.fuel_level || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Vehicle Height</p>
                <p className="text-lg font-semibold">{formatVehicleHeight(inspection.vehicle_height)}</p>
              </div>
            </div>
          </div>

          {/* Inspection Details Section */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div>
              <p className="text-sm text-gray-600">Vehicle</p>
              <p className="text-lg font-semibold">{inspection.vehicle_registration}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Driver</p>
              <p className="text-lg font-semibold">{inspection.driver_name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Start Time</p>
              <p className="text-lg">{new Date(inspection.start_time).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Duration</p>
              <p className="text-lg flex items-center gap-2">
                <span>{inspection.duration ? `${Math.floor(inspection.duration / 60)}m ${inspection.duration % 60}s` : 'N/A'}</span>
                {inspection.status === 'incomplete' && (
                  <span className="text-red-600" title="Completed before minimum time requirement">
                    ⚠
                  </span>
                )}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Status</p>
              <span className={`inline-block px-3 py-1 text-sm rounded ${
                inspection.status === 'completed' ? 'bg-green-100 text-green-800' :
                inspection.status === 'incomplete' ? 'bg-orange-100 text-orange-800' :
                inspection.status === 'failed' ? 'bg-red-100 text-red-800' :
                'bg-yellow-100 text-yellow-800'
              }`}>
                {inspection.status === 'incomplete' ? 'incomplete' : inspection.status}
              </span>
            </div>
            <div>
              <p className="text-sm text-gray-600">Defects Found</p>
              <p className={`text-lg font-semibold ${
                inspection.defects_found > 0 ? 'text-red-600' : 'text-green-600'
              }`}>
                {inspection.defects_found}
              </p>
            </div>
          </div>

          {/* Checklist Items Section */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-3 text-gray-900">Checklist Items</h3>
            {loading ? (
              <p className="text-gray-500">Loading checklist...</p>
            ) : checklist.length === 0 ? (
              <p className="text-gray-500">No checklist items found for this inspection.</p>
            ) : (
              <ul className="space-y-2">
                {checklist.map(item => (
                  <li key={item.id} className="flex items-center bg-gray-50 rounded px-4 py-2">
                    {getStatusIcon(item.status)}
                    <span className="flex-1">{item.item_name}</span>
                    {item.notes && <span className="ml-4 text-xs text-gray-500">{item.notes}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {inspection.notes && (
            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-2">Notes</p>
              <p className="text-gray-800 bg-gray-50 p-4 rounded">{inspection.notes}</p>
            </div>
          )}

          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InspectionDetailsModal;
